import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import FunnelChart from "../../../components/v3/FunnelChart";
import TrendBars from "../../../components/v3/TrendBars";
import { bdcApi } from "../../../services/bdcApi";
import { fmt$, fmtMoneyByCurrency } from "../../../utils/format";

const FUNNEL_STAGES = [
  { key: "CLICK", label: "Click" },
  { key: "SIGNUP", label: "Signup" },
  { key: "KYC_COMPLETE", label: "KYC complete" },
  { key: "INVESTMENT", label: "Investment" },
  { key: "DEAL_LIVE", label: "Deal live" },
];

const fmtRate = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

const KpiCell = ({ label, value, tone }) => (
  <div className={`kpi${tone ? " kpi--" + tone : ""}`}>
    <div className="kpi__lbl">{label}</div>
    <div className="kpi__num">{value}</div>
  </div>
);

function BDCOverview() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    bdcApi
      .overview()
      .then((res) => {
        if (live) setData(res?.data || null);
      })
      .catch((err) => {
        if (live) setError(err?.response?.data?.message || err.message || "Failed to load overview");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const kpis = data?.kpis || {};
  const funnel = data?.funnel || {};
  const trend = data?.monthly_trend || [];
  // Per-currency payloads (2026-07-24 tester finding 4): money KPIs render one
  // figure per currency; the chart carries the CAD series with any non-CAD
  // volume called out beside it.
  const commByCur = data?.commissions_by_currency || [];
  const capitalByCur = data?.capital_by_currency || [];
  const trendByCur = data?.monthly_trend_by_currency || [];
  const nonCadTrendTotal = trendByCur
    .filter((r) => r.currency !== "CAD")
    .reduce((acc, r) => {
      acc[r.currency] = Number(acc[r.currency] || 0) + Number(r.total || 0);
      return acc;
    }, {});
  const nonCadNote = Object.entries(nonCadTrendTotal)
    .map(([cur, total]) => fmt$(total, 2, cur))
    .join(" · ");

  const funnelRows = FUNNEL_STAGES.map((s) => ({
    key: s.key,
    label: s.label,
    value: Number(funnel[s.key] || 0),
  }));

  const trendRows = trend.map((r) => ({
    key: r.month,
    label: r.month.slice(5),
    value: Number(r.total || 0),
  }));

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Overview" />
      {error && <Alert tone="danger">{error}</Alert>}
      {!loading && data && (
        <>
          <section
            className="kpi-rail"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}
          >
            <KpiCell label="Investors referred"     value={kpis.investors_referred ?? 0} />
            <KpiCell label="Deals referred"         value={kpis.deals_referred ?? 0} />
            <KpiCell label="Consultants"            value={kpis.consultants_referred ?? 0} />
            <KpiCell label="Capital raised"         value={fmtMoneyByCurrency(capitalByCur, "capital", kpis.capital_raised, 0)} tone="ok" />
            <KpiCell label="Lifetime commissions"   value={fmtMoneyByCurrency(commByCur, "lifetime", kpis.commissions_lifetime)} tone="ok" />
            <KpiCell label="Pending commissions"    value={fmtMoneyByCurrency(commByCur, "pending", kpis.commissions_pending)}  tone="warn" />
            <KpiCell label="Paid commissions"       value={fmtMoneyByCurrency(commByCur, "paid", kpis.commissions_paid)}        tone="ok" />
            <KpiCell label="Conversion rate"        value={fmtRate(kpis.conversion_rate)}    tone="info" />
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__title">Conversion funnel</div>
              <div className="card__sub mono">last 90 days</div>
            </div>
            <div className="card__body">
              <FunnelChart rows={funnelRows} />
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__title">Monthly commissions trend</div>
              <div className="card__sub mono">
                CAD{nonCadNote ? ` · plus uncharted: ${nonCadNote}` : ""}
              </div>
            </div>
            <div className="card__body">
              {trendRows.length === 0 ? (
                <Empty>No commissions yet.</Empty>
              ) : (
                <TrendBars rows={trendRows} formatValue={fmt$} />
              )}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}

export default BDCOverview;
